/**
 * Language utility for dynamic RTL/LTR and lang attribute management
 * Supports: Arabic (RTL), Hebrew (LTR), English (LTR)
 */

export type Lang = 'ar' | 'he' | 'en';

const STORAGE_KEY = 'crm_lang';
const DEFAULT_LANG: Lang = 'en'; // Default is English

/**
 * Get current language from various sources (priority order):
 * 1. localStorage
 * 2. navigator.language (auto-detect)
 * 3. default 'en'
 */
export function getLang(): Lang {
  // 1. Check localStorage (user explicitly selected)
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'ar' || stored === 'he' || stored === 'en') {
    return stored;
  }

  // 2. Auto-detect from browser language on first visit
  const browserLang = navigator.language?.toLowerCase() || '';
  if (browserLang.startsWith('he') || browserLang.startsWith('iw')) return 'he';
  if (browserLang.startsWith('ar')) return 'ar';
  
  // 3. Default to English
  return DEFAULT_LANG;
}

/**
 * Save language preference to localStorage
 */
export function setLang(lang: Lang): void {
  localStorage.setItem(STORAGE_KEY, lang);
  applyDocumentLang(lang);
}

/**
 * Apply language and direction to document
 * Arabic & Hebrew = RTL, English = LTR
 */
export function applyDocumentLang(lang: Lang): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = (lang === 'ar' || lang === 'he') ? 'rtl' : 'ltr';
  
  // Update body class for CSS targeting
  document.body.classList.remove('lang-ar', 'lang-he', 'lang-en');
  document.body.classList.add(`lang-${lang}`);
}

/**
 * Initialize language on app startup
 */
export function initLang(): Lang {
  const lang = getLang();
  applyDocumentLang(lang);
  return lang;
}

/**
 * Check if text contains Arabic characters
 */
export function hasArabic(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text);
}

/**
 * Check if text contains Hebrew characters
 */
export function hasHebrew(text: string): boolean {
  return /[\u0590-\u05FF]/.test(text);
}

/**
 * Detect primary language of text
 */
export function detectTextLang(text: string): Lang {
  if (hasArabic(text)) return 'ar';
  if (hasHebrew(text)) return 'he';
  return 'en';
}
